import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, BookmarkCheck, FileDown, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/PublicLayout";

export default function MemberDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user && typeof user === "object") api.get("/member/dashboard").then((r) => setData(r.data));
  }, [user]);

  if (user === null) return <PublicLayout><div className="px-6 py-20 font-mono text-sm">Loading…</div></PublicLayout>;
  if (user === false) return <Navigate to="/login" state={{ from: "/dashboard" }} replace />;

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16" data-testid="member-dashboard">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="eyebrow">Member Dashboard</div>
          <h1 className="font-display font-black text-[clamp(2.6rem,5.4vw,4.5rem)] mt-4 leading-[0.95] tracking-tight">
            Good day, {(user.name || "Member").split(" ")[0]}.
          </h1>
          <div className="mt-2 text-[var(--ink-soft)]">Plan: <span className="font-mono uppercase">{(data?.plan_tier || user.plan_tier || "guest")}</span></div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Link to="/luminous" className="bg-[var(--ink)] text-[var(--parchment)] p-7 hover-lift" data-testid="launch-copilot-button">
              <Sparkles size={20} className="text-[var(--brass)]" />
              <h3 className="font-display font-bold text-2xl mt-4">Launch Copilot</h3>
              <p className="mt-2 text-sm text-[var(--parchment)]/70">Ask anything. Sourced, scored, board-ready.</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--brass)]">Open <ArrowRight size={12} /></span>
            </Link>
            <Link to="/trackers" className="bg-white border border-[var(--border-soft)] p-7 hover-lift">
              <FileDown size={20} className="text-[var(--oxblood)]" />
              <h3 className="font-display font-bold text-2xl mt-4">Research</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{data?.owned_reports?.length || 0} owned reports.</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--oxblood)]">Browse <ArrowRight size={12} /></span>
            </Link>
            <Link to="/trackers" className="bg-white border border-[var(--border-soft)] p-7 hover-lift">
              <BookmarkCheck size={20} className="text-[var(--viridian)]" />
              <h3 className="font-display font-bold text-2xl mt-4">Bookmarks</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{data?.bookmarks?.length || 0} saved articles.</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--viridian)]">View <ArrowRight size={12} /></span>
            </Link>
          </div>

          {data?.bookmarks?.length > 0 && (
            <div className="mt-16">
              <div className="eyebrow">Your bookmarks</div>
              <ul className="mt-6 divide-y divide-[var(--border-soft)] bg-white border border-[var(--border-soft)]">
                {data.bookmarks.map((a) => (
                  <li key={a.slug} className="p-5">
                    <Link to={`/intelligence/${a.slug}`} className="font-display font-semibold text-lg hover:text-[var(--oxblood)]">{a.title}</Link>
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)] mt-1">{a.type}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data?.recent_copilot_sessions?.length > 0 && (
            <div className="mt-16">
              <div className="eyebrow">Recent Copilot sessions</div>
              <ul className="mt-6 divide-y divide-[var(--border-soft)] bg-white border border-[var(--border-soft)]">
                {data.recent_copilot_sessions.map((s) => (
                  <li key={s.session_id} className="p-5">
                    <div className="font-display font-semibold text-lg">{s.title || "(untitled session)"}</div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)] mt-1">{new Date(s.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
